import { Assets, Container, FederatedPointerEvent, Graphics, Point, Sprite } from 'pixi.js';
import type { IShape, ResizeHandle, ShapeTransform, ShapeType } from './interfaces/IShape';

export class ImageShape implements IShape {
    public readonly id: string;
    public name: string;
    public readonly type: ShapeType = 'image';
    public get visible(): boolean { return this.container.visible; }
    public set visible(v: boolean) { this.container.visible = v; }
    public container: Container;
    public selected: boolean = false;
    public isDragging: boolean = false;
    public isResizing: boolean = false;
    public dragOffset: Point;
    public resizeOffset: Point;
    public originalScale: Point;
    private lastKnownPos: Point;
    private needsRedraw: boolean = true;
    private sprite: Sprite;
    private imageUrl: string;
    private selectionBorder: Graphics;
    private resizeHandles: Graphics;
    private scaleX: number = 1;
    private scaleY: number = 1;
    private cachedBounds: { width: number; height: number } | null = null;
    private zoomListener: ((data: any) => void) | null = null;

    constructor(imageUrl: string, name: string = 'Image', id: string = crypto.randomUUID()) {
        this.id = id;
        this.name = name;
        this.imageUrl = imageUrl;
        this.container = new Container();
        this.selectionBorder = new Graphics();
        this.resizeHandles = new Graphics();
        this.dragOffset = new Point(0, 0);
        this.resizeOffset = new Point(0, 0);
        this.originalScale = new Point(1, 1);
        this.lastKnownPos = new Point(0, 0);
        this.sprite = new Sprite();
        this.setupInteraction();
        this.loadImage();
        // Pivot starts at (0,0); loadImage will set it to the true centre once
        // the texture dimensions are known.
    }

    private setupInteraction(): void {
        this.container.eventMode = 'static';
        this.container.cursor = 'pointer';
    }

    private async loadImage(): Promise<void> {
        try {
            const texture = await Assets.load(this.imageUrl);
            this.sprite.texture = texture;
            this.container.addChild(this.sprite);
            this.container.addChild(this.selectionBorder);
            this.container.addChild(this.resizeHandles);

            // Recalculate pivot to the true center of the loaded texture,
            // preserving the logical top-left world position set before load.
            const tw = texture.width;
            const th = texture.height;
            const topLeftX = this.container.x - this.container.pivot.x;
            const topLeftY = this.container.y - this.container.pivot.y;
            this.container.pivot.set(tw / 2, th / 2);
            this.container.x = topLeftX + tw / 2;
            this.container.y = topLeftY + th / 2;

            this.draw();
        } catch (error) {
            console.error('Failed to load image:', error);
            this.drawPlaceholder();
        }
    }

    private drawPlaceholder(): void {
        this.selectionBorder.clear();
        this.selectionBorder.fill({ color: 0xcccccc, alpha: 0.5 });
        this.selectionBorder.rect(0, 0, 100, 100);
        this.selectionBorder.fill();
        this.needsRedraw = false;
    }

    public draw(): void {
        if (!this.needsRedraw) return;

        this.selectionBorder.clear();
        this.resizeHandles.clear();

        if (this.selected) {
            const bounds = this.getBounds();
            this.selectionBorder.setStrokeStyle({width: 2, color: 0x00ff00});
            this.selectionBorder.rect(0, 0, bounds.width, bounds.height);
            this.selectionBorder.stroke();
            this.drawResizeHandles();
        }

        this.needsRedraw = false;
        this.cachedBounds = null;
    }

    private drawResizeHandles(): void {
        const bounds = this.getBounds();

        // Get viewport scale to maintain consistent handle size
        const viewportScale = this.container.parent?.scale.x || 1;
        let handleSize = 16 / viewportScale; // Scale handles inversely with zoom

        // Cap handle size to be max 25% of the smallest dimension
        const minDimension = Math.min(bounds.width, bounds.height);
        const maxHandleSize = minDimension * 0.25;
        handleSize = Math.min(handleSize, maxHandleSize);

        // Don't let handles get too small either
        handleSize = Math.max(handleSize, 8);

        const handleColor = 0x00ff00;
        const handleAlpha = 0.9;
        const strokeColor = 0xffffff;
        const strokeWidth = Math.min(2 / viewportScale, handleSize * 0.15); // Scale stroke proportionally

        // Draw corner handles with stroke
        this.resizeHandles.fill({ color: handleColor, alpha: handleAlpha });
        this.resizeHandles.setStrokeStyle({ width: strokeWidth, color: strokeColor });

        // Corner handles
        this.resizeHandles.rect(-handleSize/2, -handleSize/2, handleSize, handleSize);
        this.resizeHandles.rect(bounds.width - handleSize/2, -handleSize/2, handleSize, handleSize);
        this.resizeHandles.rect(-handleSize/2, bounds.height - handleSize/2, handleSize, handleSize);
        this.resizeHandles.rect(bounds.width - handleSize/2, bounds.height - handleSize/2, handleSize, handleSize);

        this.resizeHandles.fill();
        this.resizeHandles.stroke();

        // Draw edge handles with stroke
        this.resizeHandles.fill({ color: handleColor, alpha: handleAlpha });
        this.resizeHandles.setStrokeStyle({ width: strokeWidth, color: strokeColor });

        // Edge handles
        this.resizeHandles.rect(bounds.width/2 - handleSize/2, -handleSize/2, handleSize, handleSize);
        this.resizeHandles.rect(bounds.width/2 - handleSize/2, bounds.height - handleSize/2, handleSize, handleSize);
        this.resizeHandles.rect(-handleSize/2, bounds.height/2 - handleSize/2, handleSize, handleSize);
        this.resizeHandles.rect(bounds.width - handleSize/2, bounds.height/2 - handleSize/2, handleSize, handleSize);

        this.resizeHandles.fill();
        this.resizeHandles.stroke();

        this.drawRotateHandle();
    }

    private drawRotateHandle(): void {
        const bounds = this.getBounds();
        const viewportScale = this.container.parent?.scale.x || 1;
        // Keep the handle a fixed screen-space distance above the shape regardless of zoom or shape size.
        const offsetY = Math.max(30 / viewportScale, 24);
        const handleRadius = Math.max(6 / viewportScale, 4);
        const lineWidth = Math.max(1.5 / viewportScale, 0.5);
        const cx = bounds.width / 2;

        this.resizeHandles.setStrokeStyle({ width: lineWidth, color: 0x00ff00 });
        this.resizeHandles.moveTo(cx, 0);
        this.resizeHandles.lineTo(cx, -offsetY);
        this.resizeHandles.stroke();

        this.resizeHandles.fill({ color: 0x00ff00, alpha: 0.9 });
        this.resizeHandles.setStrokeStyle({ width: lineWidth, color: 0xffffff });
        this.resizeHandles.circle(cx, -offsetY, handleRadius);
        this.resizeHandles.fill();
        this.resizeHandles.stroke();
    }

    public position(x: number, y: number): void {
        this.container.x = x + this.container.pivot.x;
        this.container.y = y + this.container.pivot.y;
    }

    public select(): void {
        this.selected = true;
        this.needsRedraw = true;
        this.draw();

        // Listen to viewport zoom changes to update handles in real-time
        if (this.container.parent) {
            this.zoomListener = () => {
                this.needsRedraw = true;
                this.draw();
            };
            // Access viewport manager through parent's parent (ViewportManager's container)
            const viewportContainer = this.container.parent;
            if (viewportContainer && (viewportContainer as any).on) {
                (viewportContainer as any).on?.('zoomed', this.zoomListener);
            }
        }
    }

    public deselect(): void {
        this.selected = false;
        this.needsRedraw = true;
        this.draw();

        // Remove zoom listener
        if (this.zoomListener && this.container.parent) {
            const viewportContainer = this.container.parent;
            if (viewportContainer && (viewportContainer as any).off) {
                (viewportContainer as any).off?.('zoomed', this.zoomListener);
            }
            this.zoomListener = null;
        }
    }

    public startDrag(event: FederatedPointerEvent): void {
        this.isDragging = true;

        // Convert screen coordinates to world coordinates for proper offset calculation
        const worldPos = this.container.parent?.toLocal(event.global) || event.global;

        this.dragOffset.x = this.container.x - worldPos.x;
        this.dragOffset.y = this.container.y - worldPos.y;
        this.lastKnownPos.x = event.global.x;
        this.lastKnownPos.y = event.global.y;
    }

    public onDrag(event: FederatedPointerEvent): void {
        if (!this.isDragging) return;

        // Convert screen coordinates to world coordinates
        const worldPos = this.container.parent?.toLocal(event.global) || event.global;

        // Set position directly using the drag offset
        this.container.x = worldPos.x + this.dragOffset.x;
        this.container.y = worldPos.y + this.dragOffset.y;
    }

    public endDrag(): void {
        this.isDragging = false;
    }

    public startResize(_event: FederatedPointerEvent, _handle: ResizeHandle): void {
        this.isResizing = true;
    }

    public endResize(): void {
        this.isResizing = false;
    }

    public getBounds(): { width: number; height: number } {
        if (this.cachedBounds && !this.needsRedraw) {
            return this.cachedBounds;
        }

        const tw = this.sprite.texture?.width || 100;
        const th = this.sprite.texture?.height || 100;
        this.cachedBounds = {
            width: tw * this.scaleX,
            height: th * this.scaleY
        };
        return this.cachedBounds;
    }

    public setSize(width: number, height: number): void {
        // Preserve top-left world position when pivot changes.
        const topLeftX = this.container.x - this.container.pivot.x;
        const topLeftY = this.container.y - this.container.pivot.y;
        const tw = this.sprite.texture?.width || 100;
        const th = this.sprite.texture?.height || 100;
        this.scaleX = width / tw;
        this.scaleY = height / th;
        this.sprite.scale.set(this.scaleX, this.scaleY);
        this.container.pivot.set(width / 2, height / 2);
        this.container.x = topLeftX + this.container.pivot.x;
        this.container.y = topLeftY + this.container.pivot.y;
        this.needsRedraw = true;
        this.cachedBounds = null;
        this.draw();
    }

    public setScale(scale: number): void {
        this.scaleX = scale;
        this.scaleY = scale;
        this.sprite.scale.set(scale);
        this.needsRedraw = true;
        this.cachedBounds = null;
        this.draw();
    }

    public getScale(): number {
        return this.scaleX;
    }

    public getTransform(): ShapeTransform {
        const bounds = this.getBounds();
        return {
            x: this.container.x - this.container.pivot.x,
            y: this.container.y - this.container.pivot.y,
            width: bounds.width,
            height: bounds.height,
            rotation: this.container.rotation,
        };
    }

    public setTransform(transform: ShapeTransform): void {
        this.setSize(transform.width, transform.height);
        this.container.x = transform.x + this.container.pivot.x;
        this.container.y = transform.y + this.container.pivot.y;
        this.container.rotation = transform.rotation ?? 0;
    }
}